import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ISuppliersApiService } from './suppliers-api.interface';

import { environment } from '@orderna/admin-panel/src/environments/environment';
import { SuccessResponse } from '../shared/success-response';
import { FindSuppliersQueryDto } from './dtos/find-suppliers.query.dto';
import { FindSuppliersResponseDto } from './dtos/find-suppliers.response.dto';
import { FindSupplierResponseDto } from './dtos/find-supplier.response.dto';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { ExportSuppliersQueryDto } from './dtos/export-suppliers.query.dto';
import { FindSupplierNamesResponseDto } from './dtos/find-supplier-names.response.dto';
import { ExportExcelPdfService } from '../export-excel-pdf/export-excel-pdf.service';
import { CreateSupplierRequestDto } from './dtos/create-supplier.request.dto';
import { CreateSupplierResponseDto } from './dtos/create-supplier.response.dto';
import { UpdateSupplierRequestDto } from './dtos/update-supplier.request.dto';
import { UpdateSupplierResponseDto } from './dtos/update-supplier.response.dto';
import { FindSupplierPurchaseAutofillResponseDto } from './dtos/find-supplier-purchase-autofill.response.dto';
import { FindSupplierPurchaseAutofillQueryDto } from './dtos/find-supplier-purchase-autofill.query.dto';

@Injectable({
  providedIn: 'root',
})
export class SuppliersApiService implements ISuppliersApiService {
  private http = inject(HttpClient);
  private readonly API_URL = `${environment.api}/suppliers`;
  private exportService = inject(ExportExcelPdfService);

  async findSuppliers(
    query: FindSuppliersQueryDto,
  ): Promise<FindSuppliersResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindSuppliersResponseDto>>(this.API_URL, {
        params,
      }),
    );

    return response.data;
  }

  async exportSuppliers(
    query: ExportSuppliersQueryDto,
    options?: HandleBlobHttpResponseOptions,
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(`${this.API_URL}/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      }),
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  async findSupplierNames() {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindSupplierNamesResponseDto>>(
        `${this.API_URL}/names`,
      ),
    );

    return response.data;
  }

  async findSupplierPurchaseAutofill(
    supplierId: number,
    query: FindSupplierPurchaseAutofillQueryDto,
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindSupplierPurchaseAutofillResponseDto>>(
        `${this.API_URL}/${supplierId}/purchase-autofill`,
        { params },
      ),
    );

    return response.data;
  }

  async findSupplier(supplierId: number) {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindSupplierResponseDto>>(
        `${this.API_URL}/${supplierId}`,
      ),
    );

    return response.data;
  }

  async createSupplier(
    data: CreateSupplierRequestDto,
  ): Promise<CreateSupplierResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateSupplierResponseDto>>(
        `${this.API_URL}`,
        data,
      ),
    );

    return response.data;
  }

  async updateSupplier(
    supplierId: number,
    data: UpdateSupplierRequestDto,
  ): Promise<UpdateSupplierResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdateSupplierResponseDto>>(
        `${this.API_URL}/${supplierId}`,
        data,
      ),
    );

    return response.data;
  }

  async deleteSupplier(supplierId: number): Promise<void> {
    await firstValueFrom(
      this.http.delete<SuccessResponse>(`${this.API_URL}/${supplierId}`),
    );
  }

  async deleteSuppliers(supplierIds: number[]): Promise<void> {
    await firstValueFrom(
      this.http.delete<SuccessResponse>(`${this.API_URL}/delete-many`, {
        body: { ids: supplierIds },
      }),
    );
  }
}
